/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react'
import {
  Dialog,
  IconButton,
  Typography,
  Tab,
  Button,
  useTheme,
  useMediaQuery
} from '@material-ui/core'
import { TabContext, TabList, TabPanel } from '@material-ui/lab'
import CloseIcon from '@material-ui/icons/Close'

import { useReviewModalStyles } from './review-modal.styles'
import { ReviewItem } from '../review-item'
import { useRouter } from 'next/router'
import { PROFILE_TYPE, REVIEW_TYPE } from './profiles.constants'
import {
  // getServiceURL,
  SERVICES_URL,
  ProfileUser,
  useTranslation,
  useLocale
} from '@plandi/common'
import { components } from '@plandi/common/src/generated'
import { useQueryProfileReviews } from '../common.queries'

type Props = {
  handleClose: () => void
  open: boolean
  profileName?: string
  profileId: number
  profileType?: PROFILE_TYPE | any
  profileSlugName?: string
  user?: ProfileUser
  profileSpecialist?: components['schemas']['ProfileSpecialistMeResource']
}

type TabType = 'GOOD' | 'NEUTRAL' | 'BAD'

export const ReviewsModal: FC<Props> = ({
  handleClose,
  open,
  profileName,
  profileId,
  profileType,
  profileSlugName,
  user,
  profileSpecialist
}) => {
  const { data: translate } = useTranslation()
  const [tabValue, setTabValue] = useState<TabType>('GOOD')

  const REVIEW_TYPE_VALUES = {
    [REVIEW_TYPE.GOOD]: translate?.REVIEW_TYPE_VALUES?.GOOD ?? 'Хорошие',
    [REVIEW_TYPE.NEUTRAL]:
      translate?.REVIEW_TYPE_VALUES?.NEUTRAL ?? 'Нейтральные',
    [REVIEW_TYPE.BAD]: translate?.REVIEW_TYPE_VALUES?.BAD ?? 'Плохие'
  }

  const classes = useReviewModalStyles()

  const { data: reviews } = useQueryProfileReviews(profileId, {
    enabled: !!profileId
  })
  const locale = useLocale()
  const url = SERVICES_URL
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const router = useRouter()

  const negativeReviews = reviews?.filter((review) => review?.estimate < 3)
  const mediumReviews = reviews?.filter((review) => review?.estimate === 3)
  const goodReviews = reviews?.filter((review) => review?.estimate > 3)

  const profileTypeSlogan =
    profileType === PROFILE_TYPE.SPECIALIST
      ? '-specialist'
      : PROFILE_TYPE.COMPANY
      ? '-company'
      : '-factory'
  const profileLink =
    locale === 'ru'
      ? `${url.job + '/ru' + '/profile'}` +
        `${profileTypeSlogan}/${profileSlugName}/${profileId}`
      : `${url.job + '/profile'}` +
        `${profileTypeSlogan}/${profileSlugName}/${profileId}`

  return (
    <Dialog
      className={classes.root}
      maxWidth="lg"
      onClose={handleClose}
      open={open}
      fullScreen={isMobileScreen}
      fullWidth={isMobileScreen}
    >
      <TabContext value={tabValue}>
        <div className={classes.modalHeader}>
          {!isMobileScreen ? (
            <Typography variant="h5" className={classes.modalTitle}>
              {profileName}
            </Typography>
          ) : (
            <div className={classes.modalHeaderTop}>
              <Typography variant="h5" className={classes.modalTitle}>
                {profileName}
              </Typography>
              <IconButton
                disableRipple
                disableFocusRipple
                disableTouchRipple
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </div>
          )}
          <div className={classes.modalHeaderRight}>
            <TabList
              textColor="secondary"
              onChange={(_, value) => {
                setTabValue(value)
              }}
              className={classes.tabs}
            >
              {Object.entries(REVIEW_TYPE_VALUES).map(([key, value]) => (
                <Tab
                  key={key}
                  label={value}
                  value={key}
                  className={classes.tab}
                  onClick={() => setTabValue(key as TabType)}
                />
              ))}
            </TabList>
            {!isMobileScreen && (
              <IconButton
                disableRipple
                disableFocusRipple
                disableTouchRipple
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            )}
          </div>
        </div>
        <div className={classes.tabPanels}>
          <TabPanel className={classes.tabPanel} value={REVIEW_TYPE.GOOD}>
            {goodReviews?.map((rev) => (
              <ReviewItem key={rev.id} review={rev} />
            ))}
          </TabPanel>
          <TabPanel className={classes.tabPanel} value={REVIEW_TYPE.NEUTRAL}>
            {mediumReviews?.map((rev) => (
              <ReviewItem key={rev.id} review={rev} />
            ))}
          </TabPanel>
          <TabPanel className={classes.tabPanel} value={REVIEW_TYPE.BAD}>
            {negativeReviews?.map((rev) => (
              <ReviewItem key={rev.id} review={rev} />
            ))}
          </TabPanel>
        </div>
        <div className={classes.modalBottom}>
          <Button
            variant="text"
            color="inherit"
            className={classes.backBtn}
            onClick={handleClose}
          >
            {translate?.['back-to-page'] ?? 'Обратно на страницу'}
          </Button>
          {!!user && profileSpecialist?.id !== profileId && (
            <Button
              className={classes.leaveReviewBtn}
              variant="contained"
              color="secondary"
              onClick={() => router.push(profileLink)}
            >
              {translate?.['leave-feedback'] ?? 'Оставить отзыв'}
            </Button>
          )}
        </div>
      </TabContext>
    </Dialog>
  )
}
